<script setup lang="ts">
import { defineLayoutOfProduct } from '@/utils/defineLayoutOfProduct'

import type { Product } from '@/types/api/api-v3/product'
import type { OneProductV5 } from '@/types/api/api-v5/product'
import { NAME_PARENT_COMPONENT } from '@/types/products'

import ProductContent from '@/components/Product/ProductItem/ProductContent/ProductContent.vue'
import ProductImage from '@/components/Product/ProductItem/ProductImage/ProductImage.vue'
import ProductTags from '@/components/Product/ProductItem/ProductTags/ProductTags.vue'

type Props = {
  product: Product | OneProductV5
  parentComponent: string
  isAvailable: boolean
}
const props = defineProps<Props>()

const isMainPage = computed(
  () =>
    NAME_PARENT_COMPONENT.MAIN_DESKTOP === props.parentComponent ||
    NAME_PARENT_COMPONENT.MAIN_MOBILE === props.parentComponent,
)

const currentModification = ref(props.product?.modifications[0] || {}) // текущая модификация товара

watch(
  () => props.product,
  (newProduct) => {
    currentModification.value = newProduct?.modifications[0] || {}
  },
)

const getProductMicrolayouts = defineLayoutOfProduct(props.parentComponent)

const itemScopeProductItem = getProductMicrolayouts(true)
const itemTypeProductItem = getProductMicrolayouts('http://schema.org/Product')

const changeModification = (modificationId: number) => {
  // меняем текущую модификацию продукта путем поиска по id модификации
  currentModification.value =
    props.product.modifications.find(
      (modification) => modification.id === modificationId,
    ) || props.product.modifications[0]
}
</script>

<template>
  <div
    :itemscope="itemScopeProductItem"
    :itemtype="itemTypeProductItem"
    class="product"
  >
    <product-image
      :product="product"
      :current-modification="currentModification"
      :parent-component="parentComponent"
      :is-available="isAvailable"
    />

    <product-tags :product="product" :parent-component="parentComponent" />

    <product-content
      :product="product"
      :is-main-page="isMainPage"
      :current-modification="currentModification"
      :parent-component="parentComponent"
      :is-available="isAvailable"
      @change-modification="changeModification"
    />
  </div>
</template>

<style scoped lang="scss">
.product {
  position: relative;
  display: grid;
  grid-template-columns: 1fr max(452px);
  gap: 0 70px;
  align-items: center;
  margin: 50px 0 90px;

  @include media('<=desktop-mini') {
    margin: 26px 0;
  }

  @include media('<=desktop') {
    grid-template-columns: 1fr;
    padding-bottom: 0;
  }
}
</style>
